import { useInfiniteQuery, useQuery } from 'react-query';

import type { ShowcaseProductsParams } from '~/api/data-source/showcase';
import type { MarketProductItem } from '~/api/model';
import { SHOWCASE_QUERY_KEY } from '~/api/query-keys';

export type { ShowcaseProductsParams } from '~/api/data-source/showcase';

// 쇼케이스 메타/상품 목록은 실시간성이 낮다. HydratePrefetch 프리페치 후
// mount 시 즉시 background refetch 되는 것을 막기 위해 staleTime 을 부여한다.
const SHOWCASE_STALE_TIME = 5 * 60 * 1000;

/** 쇼케이스 그룹 메타 (이름/이미지/쇼케이스 탭 목록) */
// GET /api/market-place/v1/showcase-groups/{showcaseGroupCode}
export const useShowcaseGroup = (showcaseGroupCode: string) => {
  const { queryKey, queryFn } = SHOWCASE_QUERY_KEY.group(showcaseGroupCode);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!showcaseGroupCode,
    keepPreviousData: true,
    staleTime: SHOWCASE_STALE_TIME,
  });
};

/** 쇼케이스 상품 목록 (무한 스크롤) */
// GET /api/market-place/v4/showcases
export const useInfiniteShowcaseProducts = (params: ShowcaseProductsParams) => {
  const { queryKey, queryFn } = SHOWCASE_QUERY_KEY.products(params);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(queryKey, queryFn, {
      suspense: true,
      keepPreviousData: true,
      staleTime: SHOWCASE_STALE_TIME,
      enabled: !!params.code,
      getNextPageParam: (lastPage) => {
        if (!lastPage.products?.hasNextPage) return undefined;
        return lastPage.products.searchAfter;
      },
    });

  const products = data?.pages?.reduce<MarketProductItem[]>(
    (acc, page) => acc.concat(page.products?.content ?? []),
    [],
  );

  // count 는 첫 페이지 응답 기준
  const count = data?.pages?.[0]?.count ?? 0;

  return { products, count, fetchNextPage, hasNextPage, isFetchingNextPage };
};
